// src/pages/BrandDetail/BrandDetailPage.js - New UI Design
import { useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { getBrandDetails } from '../../constants/brandService';
import './BrandDetailPage.css';
import { BrandDetailLoadingSkeleton } from '../../components/common/UI';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
} from 'chart.js';

// New component imports
import BrandSummarySection from './components/BrandSummarySection';
import ReviewMetricsSection from './components/ReviewMetricsSection';
import BrandActivitySection from './components/BrandActivitySection';
import PremiumMetricsSection from './components/PremiumMetricsSection';
import FeaturedProductsSection from './components/FeaturedProductsSection';

import { decodeBrandId } from '../../utils/urlUtils';
import { useLanguage } from '../../contexts/LanguageContext';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
);

const BrandDetailPage = () => {
  const { translate, language } = useLanguage();
  const { brandId } = useParams();
  const actualBrandId = decodeBrandId(brandId);

  const [brandDetails, setBrandDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBrandDetails = async () => {
      try {
        setLoading(true);
        const apiLanguage = ['ko', 'en', 'vi'].includes(language) ? language : 'ko';
        const data = await getBrandDetails(actualBrandId, apiLanguage);
        setBrandDetails(data);
        setLoading(false);
      } catch (err) {
        setError(err.message || 'Failed to load brand details');
        setLoading(false);
      }
    };

    fetchBrandDetails();
  }, [actualBrandId, language]);

  if (loading) return <BrandDetailLoadingSkeleton />;
  if (error) return <div className="error">{error}</div>;
  if (!brandDetails) return <div className="error">{translate('brandDetailPage.notFound')}</div>;

  return (
    <div className="brand-detail-page">
      <div className="brand-detail-container">
        {/* Section 1: Brand Summary */}
        <BrandSummarySection brandDetails={brandDetails} />

        {/* Section 2: Brand Review Trends */}
        <ReviewMetricsSection reviewMetric={brandDetails.review_metric} />

        {/* Section 3: Brand Activity */}
        <BrandActivitySection brandDetails={brandDetails} />

        {/* Section 4: Price Positioning */}
        <PremiumMetricsSection premiumMetric={brandDetails.premium_metric} />

        {/* Section 5: Featured Products */}
        <FeaturedProductsSection products={brandDetails.product_list} />
      </div>
    </div>
  );
};

export default BrandDetailPage;
